import { Directive, HostListener, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';

@Directive({
    selector: '[libPhoneFormat]',
    standalone: true
})
export class PhoneFormatDirective implements OnChanges {

    /**
     * The accepted key codes for input.
     *
     * @private
     * @memberof PhoneFormatDirective
     */
    private readonly acceptedInputKeyCodes = ['Digit0', 'Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5', 'Digit6', 'Digit7', 'Digit8', 'Digit9', 'Numpad0', 'Numpad1', 'Numpad2', 'Numpad3', 'Numpad4', 'Numpad5', 'Numpad6', 'Numpad7', 'Numpad8', 'Numpad9'];

    /**
     * A mapping of input keys to their corresponding key codes.
     */
    private readonly inputKeyToCodeMapping: { [key: string]: string } = {
        '0': 'Digit0',
        '1': 'Digit1',
        '2': 'Digit2',
        '3': 'Digit3',
        '4': 'Digit4',
        '5': 'Digit5',
        '6': 'Digit6',
        '7': 'Digit7',
        '8': 'Digit8',
        '9': 'Digit9',
        'Numpad0': 'Numpad0',
        'Numpad1': 'Numpad1',
        'Numpad2': 'Numpad2',
        'Numpad3': 'Numpad3',
        'Numpad4': 'Numpad4',
        'Numpad5': 'Numpad5',
        'Numpad6': 'Numpad6',
        'Numpad7': 'Numpad7',
        'Numpad8': 'Numpad8',
        'Numpad9': 'Numpad9'
    };

    /**
     * The accepted special key codes.
     *
     * @private
     * @memberof PhoneFormatDirective
     */
    private readonly acceptedSpecialKeyCodes = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Control', 'Meta', 'KeyA', 'KeyC', 'KeyV', 'KeyX', 'Tab'];

    /**
     * The accepted action key codes.
     *
     * @private
     * @memberof PhoneFormatDirective
     */
    private readonly actionKeyCodes = ['KeyA', 'KeyC', 'KeyV', 'KeyX'];

    /**
     * The format of the phone number.  
     * The format can be changed by changing the values in the array.  
     * "+27 (xx) xxx-xxxx"  
     * x = digit  
     * 
     * @note The country code is set to South Africa's country code and must be changes in the onKeyDown method if modified.
     * @private
     * @memberof PhoneFormatDirective
     */
    private formatArr = ['+', '2', '7', ' ', '(', 'x', 'x', ')', ' ', 'x', 'x', 'x', '-', 'x', 'x', 'x', 'x'];

    /**
     * The index array for the phone number format.
     * Used to iterate over the input and format array to format the phone number by index.
     * 
     * @private
     * @memberof PhoneFormatDirective
     */
    private indexArr = Array.from({ length: this.formatArr.length }, (v, k) => k);

    /**
     * The form control for the input.
     *
     * @type {FormControl}
     * @memberof PhoneFormatDirective
     */
    @Input({ required: true })
    public control!: FormControl;

    constructor(private el: ElementRef) { }

    public ngOnChanges(changes: SimpleChanges): void {

        // If the control is set for the first time and has a value, format it.
        if (changes['control']?.isFirstChange()) {
            const currentValue = this.control.value as string | undefined;

            if (currentValue && currentValue.length > 0) {
                // Format the value if it's not empty.

                const startsWithCountryCode = currentValue.startsWith('+27');
                let formatted = currentValue;

                // Remove country code and replace with 0.
                if (startsWithCountryCode) formatted = '0' + currentValue.slice(3);

                // Remove all non-digit characters.
                formatted = formatted.replace(/\D/g, '');

                // Replace the first character with the country code.
                formatted = '+27' + formatted.slice(1);

                // Format the normalize number to the format.
                for(let i = 0; i < this.formatArr.length; i++) {
                    const currentChar = formatted.charAt(i);
                    const expectedChar = this.formatArr[i];
                    if (expectedChar !== 'x' && currentChar !== expectedChar) {
                        formatted = formatted.slice(0, i) + expectedChar + formatted.slice(i);
                    }
                }

                this.control.setValue(formatted, { emitEvent: false });
            }
        }
    }

    @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
        // Ignore event if the control is disabled or readonly.
        if (this.control.disabled || this.el.nativeElement.readOnly || this.el.nativeElement.disabled) return;

        const input = {
            code: event.code,
            key: event.key,
            selectionStart: (event.target as HTMLInputElement).selectionStart,
        };

        // Mobile input does not have event.code, so we'll add if it's a digit.
        if (!input.code && this.inputKeyToCodeMapping[event.key]) {
            input.code = this.inputKeyToCodeMapping[event.key];
        }

        const rawCurrentValue = this.control?.value as string | undefined;
        const inputValue = input.key;
        const cursorIndex = input.selectionStart as number; // Should be +1 of the currentIndex.

        // Backspace, delete, arrow keys, and ctrl/cmd + a/c/v/x etc.
        if (this.acceptedSpecialKeyCodes.indexOf(input.code) !== -1) {
            // Prevent input, but allow the special keys.
            event.stopPropagation();
            // If the key is A, C, V, or X, prevent its input while allowing the event to happen. Do this by resetting the value to the current value.
            if (this.actionKeyCodes.indexOf(input.code) !== -1 || (cursorIndex < 6 && cursorIndex !== 0)) {
                setTimeout(() => { this.control.setValue(rawCurrentValue); });
            }
            return;

        }
        // Digits and numpad digits.
        else if (this.acceptedInputKeyCodes.indexOf(input.code) !== -1) {
            event.preventDefault(); // Prevent input to take manual control of it.
            const currentIndex = (rawCurrentValue ? rawCurrentValue.length - 1 : 0);

            // Check if the country code is set and set it if it is not.
            let { shouldExit, updatedCurrentValue } = this.checkAndSetCountryCode(rawCurrentValue, inputValue);

            if (!shouldExit && cursorIndex === currentIndex + 1) {
                updatedCurrentValue += inputValue;
            } else if (!shouldExit) {
                const result = this.replaceCharAtCursorIndex(updatedCurrentValue, cursorIndex, inputValue);
                shouldExit = result.shouldExit;
                updatedCurrentValue = result.updatedCurrentValue;
            }

            if (shouldExit) return;

            const formatted = this.format(updatedCurrentValue);

            this.control.setValue(formatted);
        }
        // Prevent all other keys.
        else {
            event.preventDefault();
        }
    }

    /**
     * Formats the input value to the format "+27 (xx) xxx-xxxx" where x is a digit.
     *
     * @private
     * @param {string} updatedCurrentValue The updated current value of the input.
     * @return {string} The formatted value.
     * @memberof PhoneFormatDirective
     */
    private format(updatedCurrentValue: string) {
        let newVal = '';
        // Automatically adjust the input to format into the format;
        for (let i = 0; i < this.indexArr.length; i++) {
            const currentChar = updatedCurrentValue.charAt(i);
            let expectedChar = this.formatArr[i];
            const isDigit = isNum(currentChar);
            const isEqualOrRightKey = currentChar === expectedChar || expectedChar === 'x' && isDigit;
            // console.log(i, 'Char C: "' + currentChar + '", E: "' + expectedChar + '". isDigit:', isDigit, '. isEqualOrRightKey', isEqualOrRightKey);

            // Ensure the first section of country code is set.
            if (i > updatedCurrentValue.length - 1) continue;

            // Current character should be the expected character. Where "x" is a digit.
            if (!isEqualOrRightKey) {
                let run = 0;
                while (expectedChar !== 'x' && run < this.indexArr.length - i) {
                    run++;
                    newVal += expectedChar;
                    expectedChar = this.formatArr[i + run];
                }
                if (expectedChar === 'x' && isDigit) newVal += currentChar;

            } else if (currentChar) {
                newVal += currentChar;
            } else break;
        }
        return newVal;
    }

    /**
     * Checks if the country code is set and sets it if it is not.
     *
     * @private
     * @param {string} currentValue The current value of the input.
     * @param {string} inputValue The input value.
     * @return { { shouldExit: boolean, updatedCurrentValue: string } } Indicator if should exit the method and the updated currentValue.
     * @memberof PhoneFormatDirective
     */
    private checkAndSetCountryCode(currentValue: string | undefined, inputValue: string) {
        if (!currentValue || currentValue.length === 0) {
            currentValue = '+27 (';

			// If it's 0, add only the country code.
            if (inputValue === '0') {
                this.control.setValue(currentValue);
                return { shouldExit: true, updatedCurrentValue: currentValue };
            }
			// Else if the input is a number, add the country code and the input value.
			else {
				currentValue += inputValue;
				this.control.setValue(currentValue);
				return { shouldExit: true, updatedCurrentValue: currentValue };
			}
        }

        return { shouldExit: false, updatedCurrentValue: currentValue };
    }

    /**
     * Replaces the character at the cursor index with the expected character or the input value.
     *
     * @private
     * @param {string} currentValue The current value of the input.
     * @param {number} cursorIndex The cursor index.
     * @param {string} inputValue The input value.
     * @return { { shouldExit: boolean, updatedCurrentValue: string } } Indicator if should exit the method and the updated currentValue.
     * @memberof PhoneFormatDirective
     */
    private replaceCharAtCursorIndex(currentValue: string, cursorIndex: number, inputValue: string) {
        //  If the cursor is not set in the country code.
        if (cursorIndex > 4) {
            const formatChar = this.formatArr[cursorIndex];
            const char = currentValue.charAt(cursorIndex);

            // If the character at the cursor index is the same as the expected character, move the cursor to the next valid input character.
            if (formatChar !== 'x' && formatChar === char) {
                // Set the cursor to the next valid input character.
                this.setCursor(cursorIndex + 1);
                // Inform the caller method to exit.
                return { shouldExit: true, updatedCurrentValue: currentValue };
            }
            // Ensure the character being replaced is not a special/formatting character.
            else if (formatChar !== 'x') {
                this.setCursor(cursorIndex + 1);
                // Insert the expected character at the cursor index.
                currentValue = currentValue.slice(0, cursorIndex) + formatChar + currentValue.slice(cursorIndex);
                // Update the form control value with the expected character.
                this.control.setValue(currentValue);
                // Inform the caller method to exit.
                return { shouldExit: true, updatedCurrentValue: currentValue }
            } else {
                // Insert the input value at the cursor index.
                currentValue = currentValue.slice(0, cursorIndex) + inputValue + currentValue.slice(cursorIndex);
                // Set the cursor to the next index.
                this.setCursor(cursorIndex + 1);
            }
        } else {
            // Set the cursor to the next valid input character.
            this.setCursor(6);
            // Inform the caller method to exit.
            return { shouldExit: true, updatedCurrentValue: currentValue };
        }

        // False = Do not exit the caller method after this returns.
        return { shouldExit: false, updatedCurrentValue: currentValue };
    }


    /**
     * Sets the cursor to the specified index on the input element.
     *
     * Wrapped in setTimeout to allow the input to be updated before setting the cursor.
     * 
     * @private
     * @param {number} cursorIndex The index to set the cursor to.
     * @memberof PhoneFormatDirective
     */
    private setCursor(cursorIndex: number) {
        setTimeout(() => {
            (this.el.nativeElement as HTMLInputElement).setSelectionRange(cursorIndex, cursorIndex);
        });
    }
}

/**
 * Checks if the value is a number using Regex.
 * Don't replace this with a isNaN or ParseInt method, as that returns 0/true for strings of ' '.
 *
 * @param {string} val The value to check.
 * @return {boolean} True if the value is a number, false otherwise.
 */
function isNum(val: string): boolean {
    return /^\d$/.test(val);
}